import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { Editor, EditorTextChangeEvent } from '@openng/optimus-ui/editor';
import type Quill from 'quill';

@Component({
  selector: 'app-note-editor',
  imports: [Editor, ReactiveFormsModule],
  templateUrl: './note-editor.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NoteEditorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoteEditorComponent implements ControlValueAccessor {
  readonly height = input('300px');
  readonly readOnly = input(false);
  readonly textChange = output<EditorTextChangeEvent>();

  protected readonly control = new FormControl<string | null>(null);
  protected readonly disabled = signal(false);
  // Only offer formats reproduced by the shared, sanitized log-entry display.
  protected readonly formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'list',
    'link',
    'image',
    'code-block',
  ];

  private readonly editor = viewChild(Editor);
  private onChange: (value: string | null) => void = () => {};
  protected onTouched: () => void = () => {};

  constructor() {
    this.control.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.onChange(value);
      this.onTouched();
    });
  }

  writeValue(value: string | null): void {
    this.control.setValue(value, { emitEvent: false });
  }

  registerOnChange(onChange: (value: string | null) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
    if (disabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }

  getQuill(): Quill | undefined {
    return this.editor()?.getQuill();
  }
}
